import React, { useMemo, useRef } from 'react';
import type { Realm, Skill, SkillState } from '../data/realms';
import IslandCanvas, { type NavTarget } from './IslandCanvas';
import { useFitScale } from '../lib/useFitScale';
import { useFocusTrap } from '../lib/useFocusTrap';
import { problemsBySkillId } from '../data/problems/_loader';

// Real problem count from the JSON bank so the badge never drifts when
// /generate-problems adds a new one. (No XP gating in alpha, so there's no
// "aspirational behind-the-gate" count to fall back to.)
function displayCountFor(skill: Skill): number {
  return problemsBySkillId(skill.id).length;
}

// Logical grid for the in-modal skill canvas. Every realm uses the same grid
// math; rows expand to fit the realm's skill count so the avatar always has
// well-spaced targets regardless of whether you're in API Avenue (7) or
// Algo Isles (15).
const COLS = 5;
const CELL_W = 180;
const CELL_H = 110;
const GAP = 16;

const STATE_ICON: Record<SkillState, string> = {
  done: '✓',
  current: '▶',
  attempted: '◐',
  todo: '○',
  locked: '🔒',
};

const STATE_FILL: Record<SkillState, string> = {
  done: 'var(--w-amber)',
  current: 'var(--w-peach)',
  attempted: 'var(--w-cream)',
  todo: 'var(--paper)',
  locked: 'var(--paper)',
};

const REALM_TINT: Record<string, string> = {
  amber: 'var(--w-amber)',
  peach: 'var(--w-peach)',
  coral: 'var(--w-coral)',
  tan:   'var(--w-tan)',
  cream: 'var(--w-cream)',
};

interface PlacedSkill {
  skill: Skill;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Props {
  realm: Realm;
  skills: Skill[];
  done: number;
  total: number;
  /** Close this modal and return to the island world map. */
  onClose: () => void;
  /** Open the SkillModal for a specific skill (Step 2 → Step 3). */
  onOpenSkill: (skillId: string) => void;
  /** Pause the embedded avatar — true while a SkillModal is layered on top. */
  paused: boolean;
}

const RealmModal: React.FC<Props> = ({ realm, skills, done, total, onClose, onOpenSkill, paused }) => {
  const tint = REALM_TINT[realm.color] ?? 'var(--w-cream)';

  // Pre-compute each tile's position. Avatar nav targets reuse these exact
  // boxes so collision detection lines up with what the user sees.
  const placements = useMemo<PlacedSkill[]>(() => {
    const out: PlacedSkill[] = [];
    for (let i = 0; i < skills.length; i++) {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      out.push({
        skill: skills[i],
        x: GAP + col * (CELL_W + GAP),
        y: GAP + row * (CELL_H + GAP),
        w: CELL_W,
        h: CELL_H,
      });
    }
    return out;
  }, [skills]);

  const rows = Math.max(1, Math.ceil(skills.length / COLS));
  const canvasW = GAP + COLS * (CELL_W + GAP);
  const canvasH = GAP + rows * (CELL_H + GAP);

  const navTargets = useMemo<NavTarget[]>(
    () =>
      placements
        .filter(p => p.skill.state !== 'locked')
        .map(p => ({
          id: p.skill.id,
          label: p.skill.name,
          x: p.x,
          y: p.y,
          w: p.w,
          h: p.h,
          route: '', // unused — onEnter handles dispatch
        })),
    [placements],
  );

  const fitRef = useRef<HTMLDivElement>(null);
  const scale = useFitScale(fitRef, canvasW, 32);
  const dialogRef = useRef<HTMLDivElement>(null);
  // While the layered SkillModal is open, *that* modal owns the focus trap;
  // pausing this one prevents both traps from fighting over Tab.
  useFocusTrap(dialogRef, !paused);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${realm.name} — ${realm.pun}`}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(30, 27, 22, 0.55)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        animation: 'sb-modal-fade 0.18s ease-out',
      }}
    >
      <div
        ref={dialogRef}
        onClick={e => e.stopPropagation()}
        className="sk-box shadow"
        style={{
          background: `color-mix(in srgb, ${tint} 22%, var(--paper))`,
          width: 'min(96vw, 1080px)',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'sb-modal-rise 0.22s cubic-bezier(0.2, 0.7, 0.3, 1.2)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 18px',
            borderBottom: '1.4px dashed var(--ink)',
            background: 'var(--paper)',
            flexShrink: 0,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 28 }}>{realm.emoji}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>{realm.name}</h2>
            <div style={{ fontStyle: 'italic', color: 'var(--ink-soft)', fontSize: 13 }}>"{realm.pun}"</div>
          </div>
          <span className={`sk-pill ${realm.color}`}>{done} / {total} skills done</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="sk-btn ghost"
            style={{ fontSize: 18, padding: '2px 10px' }}
          >
            ✕
          </button>
        </div>

        {/* Hint strip */}
        <div
          style={{
            padding: '6px 18px',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 10,
            color: 'var(--ink-faint)',
            background: 'var(--paper)',
            borderBottom: '1px solid var(--paper-3)',
            flexShrink: 0,
          }}
        >
          arrow keys move the avatar · enter to open a skill · esc to close
        </div>

        {/* Skill grid + avatar canvas */}
        <div
          ref={fitRef}
          className="scroll-y"
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: 16,
            minHeight: 0,
          }}
        >
          <div
            style={{
              position: 'relative',
              width: canvasW * scale,
              height: canvasH * scale,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: canvasW,
                height: canvasH,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            >
              {placements.map(({ skill, x, y, w, h }) => {
                const isLocked = skill.state === 'locked';
                return (
                  <div
                    key={skill.id}
                    className="sk-box shadow"
                    onClick={() => !isLocked && onOpenSkill(skill.id)}
                    style={{
                      position: 'absolute',
                      left: x,
                      top: y,
                      width: w,
                      height: h,
                      padding: 10,
                      cursor: isLocked ? 'not-allowed' : 'pointer',
                      background: isLocked ? 'var(--paper-2)' : 'var(--paper)',
                      backgroundImage: isLocked
                        ? 'repeating-linear-gradient(45deg, transparent 0 6px, rgba(30,27,22,0.18) 6px 7px)'
                        : 'none',
                      opacity: isLocked ? 0.7 : 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          border: '1.5px solid var(--ink)',
                          background: STATE_FILL[skill.state],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {STATE_ICON[skill.state]}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 12.5,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {skill.name}
                        </div>
                        <div
                          style={{
                            fontSize: 9.5,
                            fontStyle: 'italic',
                            color: 'var(--ink-soft)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          "{skill.pun}"
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: 10,
                        color: 'var(--ink-faint)',
                        marginTop: 'auto',
                      }}
                    >
                      {displayCountFor(skill)} problems
                    </div>
                  </div>
                );
              })}

              {/* Avatar driver — same character, modal-scoped coordinate space */}
              <IslandCanvas
                width={canvasW}
                height={canvasH}
                targets={navTargets}
                onEnter={t => onOpenSkill(t.id)}
                enabled={!paused}
              />
            </div>
          </div>
        </div>
      </div>

      {/* One-off keyframes for the modal entrance — kept local so we don't
          pollute App.css with single-use animations. */}
      <style>{`
        @keyframes sb-modal-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes sb-modal-rise {
          from { opacity: 0; transform: translateY(8px) scale(0.985); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default RealmModal;
